/** The trading-controls form body: instrument header, per-engine Advanced
 * the shared field rows, and the footer.
 * Split out of StrategyControlsCard.tsx to stay under the file-length
 * lint limit — purely a rendering concern, no data fetching here. */

import { type ReactElement } from "react";
import { dayPlan, type Instrument } from "@/lib/instrument";
import { NumberRow, TrailPreview, WindowRow } from "./StrategyControlsFields";
import { ControlsHeader, DayRuleHint, ItmPicker } from "./StrategyControlsHeader";
import type { ControlState } from "./strategyControlsState";

interface ControlsBodyProps {
  state: ControlState;
  onSwitchInstrument: (next: Instrument) => void;
  onUpdate: <K extends keyof ControlState>(k: K, v: ControlState[K]) => void;
  onReset: () => void;
  onSave: () => void;
  onSaveLotsAsDefault: () => void;
  savingLotsDefault: boolean;
}

export function ControlsBody({
  state,
  onSwitchInstrument,
  onUpdate,
  onReset,
  onSave,
  onSaveLotsAsDefault,
  savingLotsDefault,
}: Readonly<ControlsBodyProps>): ReactElement {
  // Which strategies still run the P47 ladder. The inputs and the preview
  // are hidden for the others, because a ladder they cannot run is worse
  // than no ladder shown at all — the preview in particular reads as a
  // stop plan ("initial SL Rs 155 -> Rs 166 -> ...") that will never
  // execute.
  //   * bias  — stopped trailing 2026-08-06 (fixed stop + swing target).
  //   * BB    — stopped 2026-08-18: its stop is an INDEX level and it
  //             parks no broker order at all, so the executor writes the
  //             trail columns NULL and the ladder never engages.
  //   * supertrend — retired 2026-09-14, the last engine that trailed.
  const trails = false;
  const todaysPlan = dayPlan(new Date());
  const overrideActive =
    state.instrument !== todaysPlan.instrument.name || state.itm !== todaysPlan.itm;
  return (
    <section className="card mb-3">
      <ControlsHeader
        instrument={state.instrument}
        itm={state.itm}
        overrideActive={overrideActive}
        onOverride={onSwitchInstrument}
      />
      <DayRuleHint instrument={state.instrument} itm={state.itm} overrideActive={overrideActive} />
      <ItmPicker
        current={state.itm}
        onSelect={(v) => {
          onUpdate("itm", v);
        }}
      />
      <FieldRows
        state={state}
        onUpdate={onUpdate}
        onSaveLotsAsDefault={onSaveLotsAsDefault}
        savingLotsDefault={savingLotsDefault}
        trails={trails}
      />
      <LadderPreview state={state} trails={trails} />
      <ControlsFooter onReset={onReset} onSave={onSave} />
    </section>
  );
}

function LadderPreview({
  state,
  trails,
}: Readonly<{ state: ControlState; trails: boolean }>): ReactElement | null {
  // Bias exits on a fixed stop plus a swing target, so there is no
  // ladder to preview. The guard lives here rather than at the call site
  // to keep ControlsBody under the complexity cap.
  if (!trails) return null;
  return (
    <TrailPreview
      initialSl={state.initialSl}
      trailTrigger={state.trailTrigger}
      trailAmount={state.trailAmount}
      trailSlippage={state.trailSlippage}
    />
  );
}

interface FieldRowsProps {
  state: ControlState;
  onUpdate: <K extends keyof ControlState>(k: K, v: ControlState[K]) => void;
  onSaveLotsAsDefault: () => void;
  savingLotsDefault: boolean;
  trails: boolean;
}

function FieldRows({
  state,
  onUpdate,
  onSaveLotsAsDefault,
  savingLotsDefault,
  trails,
}: Readonly<FieldRowsProps>): ReactElement {
  return (
    <>
      <NumberRow
        state={state}
        trails={trails}
        onLots={(v) => {
          onUpdate("lots", v);
        }}
        onTrailTrigger={(v) => {
          onUpdate("trailTrigger", v);
        }}
        onTrailAmount={(v) => {
          onUpdate("trailAmount", v);
        }}
        onTrailSlippage={(v) => {
          onUpdate("trailSlippage", v);
        }}
        onSaveLotsAsDefault={onSaveLotsAsDefault}
        savingLotsDefault={savingLotsDefault}
      />
      <WindowRows state={state} onUpdate={onUpdate} />
    </>
  );
}

function WindowRows({
  state,
  onUpdate,
}: Readonly<{
  state: ControlState;
  onUpdate: <K extends keyof ControlState>(k: K, v: ControlState[K]) => void;
}>): ReactElement {
  return (
    <>
      <WindowRow
        state={state}
        onWindowStart={(v) => {
          onUpdate("windowStart", v);
        }}
        onWindowEnd={(v) => {
          onUpdate("windowEnd", v);
        }}
        onDedupSeconds={(v) => {
          onUpdate("dedupSeconds", v);
        }}
      />
    </>
  );
}

interface ControlsFooterProps {
  onReset: () => void;
  onSave: () => void;
}

function ControlsFooter({ onReset, onSave }: Readonly<ControlsFooterProps>): ReactElement {
  return (
    <div className="mt-4 flex justify-end gap-2">
      <button type="button" className="btn btn-secondary" onClick={onReset}>
        Reset
      </button>
      <button type="button" className="btn btn-primary" onClick={onSave}>
        <i className="ti ti-device-floppy text-base" /> Save controls
      </button>
    </div>
  );
}
